import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";

const EVENT_NAME = "srcCopyOverridesChange";
const TABLE = "site_copy_overrides" as const;

type Overrides = Record<string, string>;

const cache: Overrides = {};
let hydrated = false;
let hydrating: Promise<void> | null = null;

function notify() {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(EVENT_NAME));
  }
}

async function hydrate(): Promise<void> {
  if (hydrated) return;
  if (hydrating) return hydrating;
  hydrating = (async () => {
    try {
      const { data, error } = await supabase.from(TABLE).select("key, value");
      if (!error && Array.isArray(data)) {
        for (const row of data as { key: string; value: string }[]) {
          cache[row.key] = row.value;
        }
      }
    } catch {
      /* fall back to defaults */
    } finally {
      hydrated = true;
      notify();
    }
  })();
  return hydrating;
}

function subscribeRealtime() {
  if (typeof window === "undefined") return;
  try {
    supabase
      .channel("site_copy_overrides_changes")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: TABLE },
        (payload: {
          eventType: string;
          new: { key?: string; value?: string } | null;
          old: { key?: string } | null;
        }) => {
          if (payload.eventType === "DELETE") {
            const k = payload.old?.key;
            if (k) delete cache[k];
          } else {
            const row = payload.new;
            if (row?.key && typeof row.value === "string") cache[row.key] = row.value;
          }
          notify();
        },
      )
      .subscribe();
  } catch {
    /* ignore */
  }
}

if (typeof window !== "undefined") {
  hydrate();
  subscribeRealtime();
}

export function getOverride(key: string): string | null {
  return Object.prototype.hasOwnProperty.call(cache, key) ? cache[key] : null;
}

// Raw override rows currently in cache for a key prefix (no defaults).
export function getOverridesByPrefix(prefix: string): Record<string, string> {
  const out: Record<string, string> = {};
  for (const k of Object.keys(cache)) {
    if (k.startsWith(prefix)) out[k] = cache[k];
  }
  return out;
}

// ?promo_preview=banner|popup
export function isPromoPreview(surface: "banner" | "popup"): boolean {
  if (typeof window === "undefined") return false;
  try {
    return new URLSearchParams(window.location.search).get("promo_preview") === surface;
  } catch {
    return false;
  }
}


export async function setOverride(key: string, value: string): Promise<void> {
  const previous = cache[key];
  cache[key] = value;
  notify();
  try {
    const { error } = await supabase
      .from(TABLE)
      .upsert({ key, value, updated_at: new Date().toISOString() }, { onConflict: "key" });
    if (error) throw error;
  } catch (err) {
    if (previous === undefined) delete cache[key];
    else cache[key] = previous;
    notify();
    console.error("setOverride failed:", err);
    throw err;
  }
}

export async function clearOverride(key: string): Promise<void> {
  const previous = cache[key];
  delete cache[key];
  notify();
  try {
    const { error } = await supabase.from(TABLE).delete().eq("key", key);
    if (error) throw error;
  } catch (err) {
    if (previous !== undefined) cache[key] = previous;
    notify();
    console.error("clearOverride failed:", err);
    throw err;
  }
}

export function useCopyOverride(key: string, fallback: string): string {
  const [value, setValue] = useState<string>(() => getOverride(key) ?? fallback);
  useEffect(() => {
    const sync = () => setValue(getOverride(key) ?? fallback);
    if (!hydrated) hydrate().then(sync);
    else sync();
    window.addEventListener(EVENT_NAME, sync);
    return () => window.removeEventListener(EVENT_NAME, sync);
  }, [key, fallback]);
  return value;
}

// Son's Island defaults — what shows when there is no override row in the DB.
export const COPY_DEFAULTS = {
  "banner.desktop":
    "🦓☀️ $99 Safari Cabins, Weekdays Now until June 11th! Plus $99 Cabanas this Saturday at Son's River Ranch — BOOK NOW 🎉🌴",
  "banner.mobile": "🦓 $99 Safari Cabins + $99 Cabanas Sat at Son's River Ranch — BOOK NOW 🌴",
  "banner.href": "https://sonsriverranch.com/",
  "banner.enabled": "true",
  "banner.startAt": "",
  "banner.endAt": "",
  "banner.bg": "",
  "banner.fg": "",
  "popup.headline": "Wanting to stay overnight on Son's Island?",
  "popup.price": "Private island cabanas from $349 / night",
  "popup.subline": "Your overnight cabana includes:",
  "popup.cta": "Book Now",
  "popup.accommodations": "island_cabana,key_cabana",
  "popup.cards": "",
  "popup.href": "/book-now/sons-island-overnight",
  "popup.image": "island-aerial-webp",
  "popup.images": "",
  "popup.accent": "",
  "popup.buttonFg": "",
  "popup.delayMs": "10000",
  "popup.enabled": "true",
  "popup.startAt": "",
  "popup.endAt": "",
} as const;

// A CSS color override is only honored when short and non-empty.
export function safeColor(value: string): string | undefined {
  const v = (value || "").trim();
  return v && v.length <= 40 ? v : undefined;
}


// Phase 2 — on/off switch + optional schedule window.
function computePromoActive(prefix: "banner" | "popup"): boolean {
  const enabled = getOverride(`${prefix}.enabled`) ?? COPY_DEFAULTS[`${prefix}.enabled`];
  if (enabled === "false") return false;

  const now = Date.now();
  const start = Date.parse(getOverride(`${prefix}.startAt`) ?? "");
  if (Number.isFinite(start) && start > now) return false;
  const end = Date.parse(getOverride(`${prefix}.endAt`) ?? "");
  if (Number.isFinite(end) && end < now) return false;

  return true;
}

export function isPromoActive(prefix: "banner" | "popup"): boolean {
  return computePromoActive(prefix);
}

export function usePromoActive(prefix: "banner" | "popup"): boolean {
  const [active, setActive] = useState<boolean>(() => computePromoActive(prefix));
  useEffect(() => {
    const sync = () => setActive(computePromoActive(prefix));
    if (!hydrated) hydrate().then(sync);
    else sync();
    window.addEventListener(EVENT_NAME, sync);
    const id = window.setInterval(sync, 60000);
    return () => {
      window.removeEventListener(EVENT_NAME, sync);
      window.clearInterval(id);
    };
  }, [prefix]);
  return active;
}


export type CopyKey = keyof typeof COPY_DEFAULTS;

export function useAllOverrides(): [Overrides, () => void] {
  const [all, setAll] = useState<Overrides>(() => ({ ...cache }));
  const refresh = useCallback(() => setAll({ ...cache }), []);
  useEffect(() => {
    const sync = () => setAll({ ...cache });
    sync();
    window.addEventListener(EVENT_NAME, sync);
    return () => window.removeEventListener(EVENT_NAME, sync);
  }, []);
  return [all, refresh];
}